import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Plus, Trash2, Wrench, X, Hash, Calendar, User } from 'lucide-react';

export default function MachinesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: '', model: '', year: new Date().getFullYear(), serial_number: '', client_name: '', notes: '' });

  const load = () => api.get('/machines').then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/machines', { ...form, year: Number(form.year) });
      toast.success(t('app.create'));
      setOpen(false);
      setForm({ brand: '', model: '', year: new Date().getFullYear(), serial_number: '', client_name: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('machines.delete_confirm'))) return;
    await api.delete(`/machines/${id}`);
    toast.success(t('app.delete'));
    load();
  };

  return (
    <div className="animate-fade-up" data-testid="machines-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#002FA7]">{t('machines.title').toUpperCase()}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">{t('machines.title')}</h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="add-machine-btn">
          <Plus className="w-4 h-4" /> {t('machines.new')}
        </button>
      </div>

      <div className="mt-8 surface overflow-hidden" data-testid="machines-list">
        {loading ? (
          <div className="p-8 text-gray-500">{t('app.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">{t('machines.no_machines')}</div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {items.map((m) => (
              <div key={m.id} className="p-4 sm:p-5 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors" data-testid={`machine-${m.id}`}>
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center shrink-0">
                    <Wrench className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg tracking-tight truncate">
                      {m.brand} <span className="text-gray-400">·</span> {m.model}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> <span className="font-mono">{m.serial_number}</span></span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {m.year}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {m.client_name}</span>
                    </div>
                    {m.notes && <div className="text-sm text-gray-600 mt-1">{m.notes}</div>}
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => remove(m.id)}
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700 active:scale-95 transition-all shrink-0"
                    data-testid={`delete-machine-${m.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('app.delete')}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white w-full sm:max-w-lg border border-gray-200 animate-fade-up" data-testid="add-machine-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="font-display font-bold text-lg">{t('machines.new')}</div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.brand')} *</label>
                  <input required className="input-base" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="form-brand" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.model')} *</label>
                  <input required className="input-base" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="form-model" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.year')} *</label>
                  <input type="number" required className="input-base font-mono" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="form-year" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.serial_number')} *</label>
                  <input required className="input-base font-mono" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="form-serial" />
                </div>
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">{t('machines.client_name')} *</label>
                <input required className="input-base" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="form-client" />
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">{t('machines.notes')}</label>
                <textarea rows={2} className="input-base" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">{t('app.cancel')}</button>
              <button type="submit" className="btn-primary" data-testid="form-submit">{t('app.save')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
